import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import { useMemo } from "react";
import { format } from "date-fns";
import ja from "date-fns/locale/ja";
import DOMPurify from "dompurify";
import { NewsDetailAPI } from "@/features/mypage/api";
import { getCategorySx } from "@/features/mypage/utils/categoryStyles";

interface NewsDetailDialogProps {
  newsId: number | null;
  onClose: () => void;
}

/**
 * HTML文字列をサニタイズし、全てのaタグに target="_blank" と rel="noopener noreferrer" を付与します。
 * @param html サニタイズ対象のHTML文字列
 * @returns 加工後のHTML文字列
 */
const setLinkAttributes = (html: string): string => {
  const doc = DOMPurify.sanitize(html, {
    RETURN_DOM_FRAGMENT: true,
  });
  doc.querySelectorAll("a").forEach((link) => {
    link.setAttribute("target", "_blank");
    link.setAttribute("rel", "noopener noreferrer");
  });
  const container = document.createElement("div");
  container.appendChild(doc);
  return container.innerHTML;
};

export const NewsDetailDialog: React.FC<NewsDetailDialogProps> = ({
  newsId,
  onClose,
}) => {
  const newsDetail = NewsDetailAPI(newsId);
  const processedDetail = useMemo(
    () => (newsDetail ? setLinkAttributes(newsDetail.detail) : null),
    [newsDetail]
  );

  return (
    <Dialog
      open={newsId !== null}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          borderRadius: "8px",
          width: {
            // 小〜中画面: 読みやすい最大幅、画面端に余白を確保
            xs: "min(600px, calc(100% - 64px))", // 64px = MUI Dialog のデフォルト左右余白 32px × 2
            // 大画面: 画面の半分を上限にしつつ、広がりすぎを防ぐ
            lg: "min(1200px, 50vw)",
          },
        },
      }}
    >
      {!newsDetail ? (
        <>
          <DialogContent
            sx={{ display: "flex", justifyContent: "center", py: 8 }}
          >
            <CircularProgress />
          </DialogContent>
          <DialogActions sx={{ justifyContent: "center", pb: 2 }}>
            <Button variant="outlined" onClick={onClose}>
              閉じる
            </Button>
          </DialogActions>
        </>
      ) : (
        <>
          <DialogTitle
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              pb: 4,
            }}
          >
            <Box sx={getCategorySx(newsDetail.category)}>
              {newsDetail.category}
            </Box>
            <Typography
              sx={{
                fontSize: "14px",
                fontWeight: 500,
                color: "darkgray.main",
              }}
            >
              {format(new Date(newsDetail.publishedAt), "yyyy/MM/dd", {
                locale: ja,
              })}
            </Typography>
          </DialogTitle>
          <DialogContent
            sx={{
              display: "flex",
              flexDirection: "column",
              pt: 1,
              px: "10%",
            }}
          >
            <Typography sx={{ fontWeight: 500, fontSize: "16px" }}>
              {newsDetail.title}
            </Typography>
            <Box
              sx={{
                overflowY: "auto",
                mt: 4,
                "& a": { wordBreak: "break-all" },
              }}
              dangerouslySetInnerHTML={{
                __html: processedDetail ?? "",
              }}
            />
          </DialogContent>
          <DialogActions sx={{ justifyContent: "center", pb: 2 }}>
            <Button variant="outlined" onClick={onClose}>
              閉じる
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};
